import { Appointment } from "../models/admissions.model";
import {
  EngageReferral,
  EngageReferralLastActivity,
} from "../services/engage-activity.service";
import { expandPatientDirectoryListRow } from "../utils/phi-patient-directory-list.util";

/**
 * ISO datetime string for Engage activity payloads and referral `last_activity`.
 * Aligns list row dates, activity feed sort keys, and global notes display when APIs use mixed names
 * (`createdDateTime`, `dateTime`, `timestamp`, etc.).
 */
export function extractEngageActivityTimestampIso(
  payload: EngageReferralLastActivity | Record<string, unknown> | null | undefined
): string | null {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const o = payload as Record<string, unknown>;
  const asTrimmedString = (v: unknown): string | null => {
    if (typeof v !== "string") {
      return null;
    }
    const s = v.trim();
    return s.length ? s : null;
  };
  const candidates: unknown[] = [
    o["createdDateTime"],
    o["created_at"],
    o["createdAt"],
    o["dateTime"],
    o["date_time"],
    o["activityDate"],
    o["activity_date"],
    o["occurredAt"],
    o["occurred_at"],
    o["timestamp"],
    o["time"],
  ];
  for (const c of candidates) {
    const s = asTrimmedString(c);
    if (s) {
      return s;
    }
    if (typeof c === "number" && Number.isFinite(c)) {
      const ms = c < 1e12 ? c * 1000 : c;
      const d = new Date(ms);
      if (!Number.isNaN(d.getTime())) {
        return d.toISOString();
      }
    }
  }
  return null;
}

/** Activity card fallback when comment/notes is empty — `mm/dd/yyyy HH:mm:ss` (24-hour). */
export function formatEngageActivityCreatedDateTime(
  payload: EngageReferralLastActivity | Record<string, unknown> | null | undefined
): string {
  const iso = extractEngageActivityTimestampIso(payload);
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${HH}:${min}:${ss}`;
}

const ENGAGE_DIRECTORY_LAST_DOS_KEYS = [
  "last_dos",
  "lastDos",
  "lastDosDateTime",
  "last_dos_date_time",
  "lastDosDate",
  "last_dos_date",
  "lastEncounterDateTime",
  "last_encounter_date_time",
  "lastEncounterDate",
  "last_encounter_date",
] as const;

const ENGAGE_APPOINTMENT_DATE_KEYS = [
  "scheduleDate",
  "schedule_date",
  "ScheduleDate",
  "appointmentDate",
  "appointment_date",
  "AppointmentDate",
  "appointmentDateTime",
  "appointment_date_time",
  "encounterDateTime",
  "encounter_date_time",
  "encounterDate",
  "encounter_date",
  "dos",
  "DOS",
  "dateOfService",
  "date_of_service",
  "serviceDate",
  "service_date",
  "scheduledDatetime",
  "scheduled_datetime",
  "scheduledDate",
  "scheduled_date",
  "visitDate",
  "visit_date",
  "scheduledAt",
  "scheduled_at",
  "dueDate",
  "due_date",
] as const;

function formatEngageAppointmentDateTimeValue(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  const datePart = `${mm}/${dd}/${yyyy}`;
  const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
  if (!hasTime) {
    return datePart;
  }
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} at ${timePart}`;
}

/** DOS date only (MM/DD/YYYY) from referral/appointment row — matches worklist DOS column. */
export function extractEngageReferralDosDateMdY(
  payload: Record<string, unknown> | null | undefined
): string {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return "";
  }
  const expanded = expandPatientDirectoryListRow(
    payload as EngageReferral
  ) as Record<string, unknown>;
  for (const key of ENGAGE_DIRECTORY_LAST_DOS_KEYS) {
    const raw = expanded[key];
    if (raw == null || String(raw).trim() === "") {
      continue;
    }
    const text = String(raw).trim();
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const dd = String(parsed.getDate()).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text)) {
      return text;
    }
  }
  for (const key of ENGAGE_APPOINTMENT_DATE_KEYS) {
    const raw = expanded[key];
    if (raw == null || String(raw).trim() === "") {
      continue;
    }
    const text = String(raw).trim();
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const dd = String(parsed.getDate()).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text)) {
      return text;
    }
  }
  return "";
}

/** Appointment / schedule datetime for Engage activity cards (`appointment_confirmed`, etc.). */
export function extractEngageActivityAppointmentDateDisplay(
  payload: Record<string, unknown> | null | undefined
): string {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return "";
  }
  for (const key of ENGAGE_APPOINTMENT_DATE_KEYS) {
    const raw = payload[key];
    if (raw == null || String(raw).trim() === "") {
      continue;
    }
    const text = String(raw).trim();
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return formatEngageAppointmentDateTimeValue(parsed);
    }
    if (/\d{1,2}\/\d{1,2}\/\d{2,4}/.test(text)) {
      return text;
    }
  }
  const timeRaw = String(
    payload["scheduleTime"] ?? payload["schedule_time"] ?? payload["time"] ?? ""
  ).trim();
  const dateOnly = String(
    payload["scheduleDate"] ?? payload["schedule_date"] ?? payload["appointmentDate"] ?? ""
  ).trim();
  if (dateOnly && timeRaw) {
    return `${dateOnly} at ${timeRaw}`;
  }
  return "";
}

export function isEngageAddedCommentType(type: string | null | undefined): boolean {
  const t = String(type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return t === "added_a_comment" || t === "added_comment";
}

export function isEngageAppointmentConfirmedType(
  type: string | null | undefined
): boolean {
  const t = String(type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return t === "appointment_confirmed" || t === "appointmentconfirmed";
}

/**
 * True when the API `type` (or equivalent) indicates a staff/internal note — not SMS/call/voicemail.
 * Used so we never classify notes from free-text keywords in {@link EngageReferralLastActivity.comment}.
 */
export function activityTypeLooksLikeInternalNote(
  type: string | null | undefined
): boolean {
  const raw = String(type ?? "").trim();
  if (!raw) {
    return false;
  }
  const t = raw.toLowerCase().replace(/_/g, " ");
  if (t === "note" || t === "memo" || t === "manual") {
    return true;
  }
  if (t.includes("internal") && t.includes("note")) {
    return true;
  }
  if (t.includes("activity") && t.includes("note")) {
    return true;
  }
  if (t.includes("staff") && t.includes("note")) {
    return true;
  }
  return false;
}

export function listActivityIsInternalNote(ref: EngageReferral): boolean {
  const la = ref.last_activity;
  if (!la) {
    return false;
  }
  const o = la as Record<string, unknown>;
  const activityTypeField = String(
    o["activityType"] ?? o["activity_type"] ?? ""
  )
    .trim()
    .toLowerCase();
  if (activityTypeField === "manual") {
    return true;
  }
  if (
    !activityTypeField.includes("notification") &&
    (/\bnote\b/.test(activityTypeField) || /\bmemo\b/.test(activityTypeField))
  ) {
    return true;
  }
  if (activityTypeLooksLikeInternalNote(la.type)) {
    return true;
  }
  const cat = String(o["category"] ?? o["activityCategory"] ?? "").toLowerCase();
  if (cat.includes("internal") && cat.includes("note")) {
    return true;
  }
  const ch = String(o["channel"] ?? "").toLowerCase().replace(/_/g, " ");
  if (ch.includes("internal note") || ch === "internal_note") {
    return true;
  }
  /** Staff-entered text with no telecom metadata (some list APIs omit `type` for notes). */
  const typ = String(la.type ?? "").trim();
  const com = String(la.comment ?? "").trim();
  const hasTelephonyMeta = !!(
    o["callType"] ||
    o["call_type"] ||
    o["isVoicemail"] ||
    o["is_voicemail"]
  );
  if (!typ && com && !hasTelephonyMeta) {
    const chLow = ch.toLowerCase();
    if (
      chLow.includes("sms") ||
      chLow.includes("text") ||
      chLow === "voice" ||
      chLow.includes("voicemail")
    ) {
      return false;
    }
    const src = String(o["source"] ?? "").toLowerCase();
    if (src === "sms" || src === "twilio" || src.includes("text message")) {
      return false;
    }
    return true;
  }
  return false;
}

export function listActivityIsVoicemail(ref: EngageReferral): boolean {
  if (listActivityIsInternalNote(ref)) {
    return false;
  }
  const la = ref.last_activity as
    | (EngageReferralLastActivity & {
        callType?: string;
        isVoicemail?: boolean;
        is_voicemail?: boolean;
      })
    | undefined;
  if (!la) {
    return false;
  }
  if (la.isVoicemail === true || la.is_voicemail === true) {
    return true;
  }
  const rawType = String(la.type || "")
    .toLowerCase()
    .replace(/_/g, " ");
  const callType = String(la.callType || "").toLowerCase();
  if (callType === "voicemail" || callType === "voice_mail") {
    return true;
  }
  if (rawType === "voicemail" || rawType.includes("voicemail")) {
    return true;
  }
  if (rawType.includes("voice mail")) {
    return true;
  }
  if (rawType.includes("vmail") || rawType.includes("v-mail")) {
    return true;
  }
  return false;
}

export function listActivityIsReminder(ref: EngageReferral): boolean {
  if (listActivityIsInternalNote(ref)) {
    return false;
  }
  const t = (ref.last_activity?.type || "").toLowerCase();
  return t === "reminder" || t.includes("reminder");
}

export function listActivityIsMissedCall(ref: EngageReferral): boolean {
  if (listActivityIsInternalNote(ref)) {
    return false;
  }
  if (listActivityIsVoicemail(ref)) return false;
  const callType = (
    ref.last_activity as { callType?: string } | undefined
  )?.callType?.toLowerCase();
  if (callType === "missed" || callType === "missed_call") return true;
  const t = (ref.last_activity?.type || "").toLowerCase();
  const c = (ref.last_activity?.comment || "").toLowerCase();
  return (
    t.includes("missed") ||
    (t.includes("call") && t.includes("missed")) ||
    c.includes("missed call")
  );
}

export function listActivityIsMessage(ref: EngageReferral): boolean {
  if (listActivityIsInternalNote(ref)) {
    return false;
  }
  if (
    listActivityIsMissedCall(ref) ||
    listActivityIsVoicemail(ref) ||
    listActivityIsReminder(ref)
  ) {
    return false;
  }
  const t = (ref.last_activity?.type || "").toLowerCase();
  return (
    t.includes("message") ||
    t.includes("sms") ||
    t.includes("chat") ||
    t.includes("text")
  );
}

/** Completed calls (not missed / voicemail / SMS) — inbound, outbound, generic `call`. */
export function listActivityIsCall(ref: EngageReferral): boolean {
  if (listActivityIsInternalNote(ref)) {
    return false;
  }
  if (
    listActivityIsMissedCall(ref) ||
    listActivityIsVoicemail(ref) ||
    listActivityIsReminder(ref) ||
    listActivityIsMessage(ref)
  ) {
    return false;
  }
  const t = (ref.last_activity?.type || "").toLowerCase().replace(/_/g, " ");
  if (!t.trim()) {
    return false;
  }
  return (
    t.includes("call") ||
    t === "outbound" ||
    t === "inbound" ||
    t.includes("dial") ||
    t.includes("phone")
  );
}

/** Single icon per row: first matching category wins. */
export function listActivityKind(
  ref: EngageReferral
): "missed" | "voicemail" | "reminder" | "message" | "note" | "call" | "other" {
  if (listActivityIsInternalNote(ref)) {
    return "note";
  }
  if (listActivityIsMissedCall(ref)) return "missed";
  if (listActivityIsVoicemail(ref)) return "voicemail";
  if (listActivityIsReminder(ref)) return "reminder";
  if (listActivityIsMessage(ref)) return "message";
  if (listActivityIsCall(ref)) return "call";
  return "other";
}

/** Font Awesome classes for normal (completed) calls — matches unified activity feed. */
export function listActivityCallIconClass(ref: EngageReferral): string {
  const t = (ref.last_activity?.type || "").toLowerCase().replace(/_/g, " ");
  if (t.includes("outbound")) {
    return "fa-solid fa-phone-arrow-up-right unified-feed-call-icon unified-feed-call-icon--outbound";
  }
  return "fa-solid fa-phone-arrow-down-left unified-feed-call-icon unified-feed-call-icon--inbound";
}

/** SMS-style activities use `comment-sms`; other messages keep the dots icon. */
export function listActivityMessageIconClass(ref: EngageReferral): string {
  const t = (ref.last_activity?.type || "").toLowerCase().replace(/_/g, " ");
  if (t.includes("sms") || t === "text") {
    return "fa-solid fa-comment-sms text-primary";
  }
  return "fa-regular fa-comment-dots text-muted";
}

/** Title-case an API token (`abc_def` → `Abc Def`); leaves spaced labels unchanged. */
export function humanizeActivityTypeToken(typeRaw: string): string {
  if (!typeRaw?.trim()) {
    return "";
  }
  return typeRaw
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function humanizeListActivityType(typeRaw: string): string {
  return humanizeActivityTypeToken(typeRaw);
}

function getListActivitySummaryLegacyShort(
  ref: EngageReferral,
  actorSuffix: string
): string {
  const type = (ref.last_activity?.type || "").toLowerCase();
  const c = ref.last_activity?.comment?.trim();
  const base = (() => {
    if (c) {
      return c;
    }
    if (type.includes("missed")) {
      return "Missed call";
    }
    if (type.includes("form")) {
      return "New form submitted";
    }
    return "New activity";
  })();
  return actorSuffix && !base.toLowerCase().includes(actorSuffix.toLowerCase())
    ? `${base} · ${actorSuffix}`
    : base;
}

/** Whether a directory row has enough data to render list activity line + date. */
export function engageReferralRowHasListActivity(
  ref: EngageReferral | null | undefined
): boolean {
  const la = ref?.last_activity;
  if (!la) {
    return false;
  }
  return !!(
    String(la.type ?? "").trim() ||
    String(la.comment ?? "").trim() ||
    extractEngageActivityTimestampIso(la)
  );
}

/** Prefer the row that already has list activity metadata (search hits are often name-only). */
export function pickRicherEngageReferralListRow(
  a: EngageReferral | null | undefined,
  b: EngageReferral | null | undefined
): EngageReferral {
  if (!a) {
    return b as EngageReferral;
  }
  if (!b) {
    return a;
  }
  const aRich = engageReferralRowHasListActivity(a);
  const bRich = engageReferralRowHasListActivity(b);
  if (aRich && !bRich) {
    return a;
  }
  if (bRich && !aRich) {
    return b;
  }
  return b;
}

/**
 * Fill list UI fields on sparse directory/search rows from a fuller row (same patient / referral id).
 */
export function mergeEngageReferralListRowForDisplay(
  primary: EngageReferral,
  fallback?: EngageReferral | null
): EngageReferral {
  const row = expandPatientDirectoryListRow(primary);
  if (!fallback) {
    return row;
  }
  const fb = expandPatientDirectoryListRow(fallback);
  const out: EngageReferral = { ...row };
  if (!engageReferralRowHasListActivity(out) && engageReferralRowHasListActivity(fb)) {
    out.last_activity = fb.last_activity;
  }
  if (!String(out.lead_status ?? "").trim() && String(fb.lead_status ?? "").trim()) {
    out.lead_status = fb.lead_status;
  }
  if (!out.engageListFirstActivityIso?.trim() && fb.engageListFirstActivityIso?.trim()) {
    out.engageListFirstActivityIso = fb.engageListFirstActivityIso;
  }
  const outN = Number(
    (out as EngageReferral & { notification_count?: number }).notification_count
  );
  const fbN = Number(
    (fb as EngageReferral & { notification_count?: number }).notification_count
  );
  if ((!Number.isFinite(outN) || outN <= 0) && Number.isFinite(fbN) && fbN > 0) {
    (out as EngageReferral & { notification_count?: number }).notification_count =
      fbN;
  }
  return out;
}

/**
 * Activity line (text only). Same logic as Engage referrals list.
 */
export function getListActivitySummary(ref: EngageReferral): string {
  const la = ref.last_activity as
    | (EngageReferralLastActivity & Record<string, unknown>)
    | undefined;
  if (!la) {
    return "—";
  }

  const typeRaw = String(la.type || "").trim();
  const t = typeRaw.toLowerCase().replace(/_/g, " ");
  const comment = String(la.comment || "").trim();
  const actor =
    [
      la.name,
      la["activityBy"],
      la["activity_by"],
      la["createdByName"],
      la["created_by_name"],
      la["userName"],
      la["user_name"],
    ]
      .map((x) => String(x ?? "").trim())
      .find((s) => s.length > 0) ?? "";
  const visitType = String(
    la.ScheduleVisitType ??
      la["scheduleVisitType"] ??
      la["schedule_visit_type"] ??
      ""
  ).trim();

  const withActor = (s: string): string =>
    actor && !s.toLowerCase().includes(actor.toLowerCase())
      ? `${s} · ${actor}`
      : s;

  if (t === "schedule" || t.includes("clinic scheduled")) {
    let head = "Schedule";
    if (visitType) {
      head += ` (${visitType})`;
    }
    if (comment) {
      return withActor(`${head} — ${comment}`);
    }
    return withActor(head);
  }

  if (t.includes("reschedule")) {
    if (comment) {
      return withActor(`Reschedule — ${comment}`);
    }
    return withActor("Reschedule");
  }

  if (listActivityIsInternalNote(ref)) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }

  if (listActivityIsMissedCall(ref)) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }
  if (listActivityIsVoicemail(ref)) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }
  if (listActivityIsReminder(ref)) {
    const head = comment || "Reminder";
    return withActor(head);
  }
  if (listActivityIsMessage(ref)) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }
  if (t.includes("form")) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }
  if (t.includes("note")) {
    return getListActivitySummaryLegacyShort(ref, actor);
  }

  if (t.includes("call") && !listActivityIsMissedCall(ref)) {
    if (comment) {
      return withActor(`Call — ${comment}`);
    }
    return withActor("Call");
  }

  const base = humanizeListActivityType(typeRaw);
  if (comment) {
    return withActor(`${base} — ${comment}`);
  }
  return withActor(base || "Activity");
}

/**
 * Formats an ISO datetime for Engage list / header “Latest activity” date (same as referrals list).
 */
export function formatListActivityDateTime(iso?: string | null): string {
  if (!iso?.trim()) {
    return "";
  }
  const d = new Date(iso.trim());
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const clock = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    return `Today, ${clock}`;
  }
  const dayNum = d.getDate();
  const monthShort = d.toLocaleString("en-US", { month: "short" });
  return `${dayNum} ${monthShort}, ${clock}`;
}

/**
 * Activity datetime beside list row name: `last_activity` timestamp when present,
 * else earliest merged feed time, else referral `createdAt`.
 */
export function getListActivityDateTime(ref: EngageReferral): string {
  const fromLastActivity = extractEngageActivityTimestampIso(ref.last_activity);
  if (fromLastActivity) {
    return formatListActivityDateTime(fromLastActivity);
  }
  const fromFeed = ref.engageListFirstActivityIso?.trim();
  if (fromFeed) {
    return formatListActivityDateTime(fromFeed);
  }
  const created = typeof ref.createdAt === "string" ? ref.createdAt.trim() : "";
  if (created) {
    return formatListActivityDateTime(created);
  }
  return "";
}

/**
 * CRM prospect header “Last Activity” — prefer newest merged timeline instant;
 * never fall back to referral `createdAt` (that belongs on “Created At”).
 */
export function getCrmHeaderLastActivityDateTime(
  ref: EngageReferral | null | undefined,
  latestTimelineIso?: string | null
): string {
  const fromTimeline = latestTimelineIso?.trim();
  if (fromTimeline) {
    return formatListActivityDateTime(fromTimeline);
  }
  if (ref) {
    const fromLastActivity = extractEngageActivityTimestampIso(ref.last_activity);
    if (fromLastActivity) {
      return formatListActivityDateTime(fromLastActivity);
    }
  }
  return "";
}

/**
 * Same mapping as {@link UnifiedActivityFeedComponent.getEngageTypeLabel} for timeline row titles.
 */
export function engageActivityTypeDisplayLabel(
  type: string | null | undefined
): string {
  const raw = String(type ?? "").trim();
  const t = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (t === "form_filled") return "New Request";
  if (t === "drip") return "Drip";
  if (t === "checked_in") return "Checked In";
  if (t === "call") return "Call";
  if (t === "schedule") return "Scheduled";
  if (t === "reschedule") return "Rescheduled";
  if (t === "voicemail") return "Voicemail";
  if (t === "missed_call" || t === "missed") return "Missed Call";
  if (t === "note") return "Note";
  if (t === "comment") return "Comment";
  if (isEngageAppointmentConfirmedType(raw)) return "Appointment Confirmed";
  if (t === "outbound") return "Outbound Call";
  if (t === "inbound") return "Inbound Call";
  if (t === "reminder") return "Reminder";
  if (t === "stage") return "Stage";
  return humanizeActivityTypeToken(raw) || "Activity";
}

/**
 * Body text beside the type label in the activity feed card (notes/comment), for search preview parity.
 */
export function getPreviewActivityUnifiedBody(ref: EngageReferral): string {
  const la = ref.last_activity as
    | (EngageReferralLastActivity & Record<string, unknown>)
    | undefined;
  if (!la) {
    return "";
  }
  const typ = String(la.type || "").toLowerCase();
  const direct = String(la.comment ?? la["notes"] ?? "").trim();
  if (typ === "stage") {
    const st = String(la.stage ?? "").trim();
    if (st && direct) {
      return `${st} - ${direct}`;
    }
    if (st) {
      return st;
    }
  }
  if (direct) {
    return direct;
  }
  const s = getListActivitySummary(ref);
  const idx = s.indexOf(" — ");
  if (idx >= 0) {
    const rest = s.slice(idx + 3).trim();
    if (rest) {
      return rest;
    }
  }
  return s;
}

/** Placeholder labels — not useful as prospect referral tile meta / badge text. */
export function isGenericReferralOverviewMetaLabel(
  text: string | null | undefined
): boolean {
  const t = String(text ?? "")
    .trim()
    .toLowerCase();
  if (!t) {
    return true;
  }
  return (
    t === "referral created" ||
    t === "new request" ||
    t === "new form submitted" ||
    t === "new activity" ||
    /^referral\s+created\.?$/.test(t)
  );
}

/**
 * Referral row `last_activity` when type is New Request (`form_filled`) — add-referral Comment field only.
 */
export function getReferralOverviewMetaFromReferralRow(
  ref: EngageReferral
): string {
  const la = ref.last_activity as
    | (EngageReferralLastActivity & Record<string, unknown>)
    | undefined;
  if (!la) {
    return "";
  }
  const typ = String(la.type || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (typ !== "form_filled") {
    return "";
  }
  const comment = String(la.comment ?? la["notes"] ?? "").trim();
  if (comment && !isGenericReferralOverviewMetaLabel(comment)) {
    return comment;
  }
  return "";
}

/** Timestamp for unified-feed-style date line (`MMM dd, yyyy h:mm a`). */
export function extractPreviewActivityDateForDisplay(
  ref: EngageReferral
): Date | null {
  const iso =
    extractEngageActivityTimestampIso(ref.last_activity) ||
    ref.engageListFirstActivityIso?.trim() ||
    (typeof ref.createdAt === "string" ? ref.createdAt.trim() : "");
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Sort key for worklist / Engage lists — newest `last_activity` first (fallback: updated/created/DOS). */
export function appointmentLatestActivitySortMs(a: Appointment): number {
  const tryParse = (v: unknown): number => {
    if (v == null) {
      return NaN;
    }
    const s = String(v).trim();
    if (!s) {
      return NaN;
    }
    const t = Date.parse(s);
    return Number.isNaN(t) ? NaN : t;
  };
  const ext = a as Appointment & {
    last_activity?: unknown;
    lastActivity?: unknown;
    LastActivity?: unknown;
    lastModified?: string;
  };
  const isoFromLa = (la: unknown): string | null => {
    if (la == null) {
      return null;
    }
    if (typeof la === "string") {
      const s = la.trim();
      return s.length ? s : null;
    }
    if (typeof la === "object" && !Array.isArray(la)) {
      return extractEngageActivityTimestampIso(la as Record<string, unknown>);
    }
    return null;
  };
  const ordered: unknown[] = [
    isoFromLa(ext.last_activity),
    isoFromLa(ext.lastActivity),
    isoFromLa(ext.LastActivity),
    a.updatedAt,
    typeof ext.last_activity === "string" ? ext.last_activity : undefined,
    ext.lastModified,
    a.createdAt,
    a.encounterDateTime,
  ];
  for (const c of ordered) {
    const t = tryParse(c);
    if (!Number.isNaN(t)) {
      return t;
    }
  }
  return 0;
}

/** Worklist grid + Engage sidebar: descending by latest activity instant. */
export function sortAppointmentsByLatestActivityDesc(rows: Appointment[]): Appointment[] {
  return [...rows].sort(
    (a, b) => appointmentLatestActivitySortMs(b) - appointmentLatestActivitySortMs(a)
  );
}
